/************************************************************************************
 * [MODULE]: PRODUCT INFORMATION
 * Main components:
 * - l-product-form: Handles form submission and cart interactions
 * - l-variant-selects: Manages variant selection and option changes
 * - l-product-controls: Controls product quantity and variant-specific logic
 * - l-product-quantity-input: Handles quantity input interactions
 *
 * Events:
 * - l:variant:changed: Fired when the variant changes
 * - l:variant:option-changed: Fired when the variant option changes
 * - l:sellingPlan:changed: Fired when the selling plan changes
 * - l:addon-products:changed: Fired when the addon products change
 *************************************************************************************/

if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			// Class properties
			quantityInput = undefined
			quantityForm = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Initialize the product state
				this.initializeProductSwapUtility()

				// Store selected selling plan
				this.selectedSellingPlanId = this.dataset.sellingPlanId
				this.productId = this.dataset.productId

				// Notify listeners that product info is loaded
				this.dispatchEvent(new CustomEvent('l:product-info:loaded', { bubbles: true }))

				// Listen for variant option changes
				this.setupEventListeners()
			}

			/**
			 * Sets up all event listeners for the component
			 */
			setupEventListeners() {
				// Variant option changes
				document.addEventListener('l:variant:option-changed', (event) => {
					const { target, selectedOptionValues, productId, sectionId } = event.detail

					if (
						!selectedOptionValues ||
						!this.contains(target) ||
						productId !== this.productId ||
						sectionId !== this.sectionId
					)
						return

					this.handleOptionValueChange(target, selectedOptionValues, null)
				})

				// Selling plan changes
				document.addEventListener('l:sellingPlan:changed', (event) => {
					const { sellingPlanId, productId, sectionId, target } = event.detail

					if (!this.contains(target) || productId !== this.productId || sectionId !== this.sectionId) return

					const variant = this.getSelectedVariant(this)
					this.selectedSellingPlanId = sellingPlanId

					this.handleOptionValueChange(event.target, null, variant)
				})
			}

			/**
			 * Adds a callback to run before processing HTML
			 * @param {Function} callback - Function to run before HTML processing
			 */
			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			/**
			 * Sets up utilities for swapping product information
			 */
			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					// Re-initialize Shopify functionality after DOM updates
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			/**
			 * Handles changes to variant options or selling plans
			 * @param {HTMLElement} target - Element that triggered the change
			 * @param {Array} selectedOptionValues - Selected variant option values
			 * @param {Object} variant - Selected variant object
			 */
			handleOptionValueChange(target, selectedOptionValues, variant) {
				// Reset form state
				this.resetProductFormState()

				// Determine product URL and fetch parameters
				const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url
				this.pendingRequestUrl = productUrl

				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

				// Fetch and render product information
				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variant, shouldFetchFullPage),
					targetId: target.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			/**
			 * Resets the product form to its initial state
			 */
			resetProductFormState() {
				if (this.productFormEl) {
					this.productFormEl.global_toggleSubmitButton(true, null, false)
					this.productFormEl.handleErrorMessage()
				}
			}

			/**
			 * Fetches and renders updated product information
			 * @param {Object} options - Render options
			 * @param {string} options.requestUrl - URL to fetch product data
			 * @param {string} options.targetId - ID of the element that triggered the fetch
			 * @param {Function} options.callback - Callback to process fetched HTML
			 */
			renderProductInfo({ requestUrl, targetId, callback }) {
				// Cancel any pending requests
				this.abortController?.abort()
				this.abortController = new AbortController()

				// Fetch updated product information
				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')
						callback(html)
					})
					.then(() => {
						// Focus the element that triggered the update
						document.querySelector(`#${targetId}`)?.focus()
					})
					.catch((error) => {
						// Only log actual errors, not aborted requests
						if (error.name !== 'AbortError') {
							console.error('Product info fetch error:', error)
						}
					})
			}

			/**
			 * Creates a callback to handle complete product swap
			 * @param {string} productUrl - URL of the product
			 * @param {boolean} updateFullPage - Whether to update the entire page
			 * @returns {Function} Callback function
			 */
			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))

					// Update URL with new variant ID
					this.updateURL(productUrl, variant?.id)

					if (updateFullPage) {
						// Update page title
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML

						// Swap the entire main content
						LTA_HTMLUpdateUtility.viewTransition(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						// Swap just the product info section
						LTA_HTMLUpdateUtility.viewTransition(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			/**
			 * Creates a callback to handle partial product info updates
			 * @param {string} productUrl - URL of the product
			 * @returns {Function} Callback function
			 */
			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)

					// Update DOM elements
					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)

					// Handle unavailable variant
					if (!variant) {
						this.setUnavailable()
						return
					}

					// Update product sections from HTML response
					this.updateProductSections(html)

					// Notify about variant change
					document.dispatchEvent(
						new CustomEvent('l:variant:changed', {
							detail: { sectionId: this.sectionId, html, productId: this.dataset.productId, variant },
						})
					)
				}
			}

			/**
			 * Updates various product sections from fetched HTML
			 * @param {Document} html - Parsed HTML document
			 */
			updateProductSections(html) {
				const updateSourceFromDestination = (id, htmlMethod = 'innerHTML', shouldHide = () => false) => {
					const source = html.getElementById(`${id}-${this.sectionId}`)
					const destination = document.getElementById(`${id}-${this.dataset.section}`)

					if (source && destination) {
						destination[htmlMethod] = source[htmlMethod]
						destination.classList.toggle('l-hidden', shouldHide(source))
					}
				}

				// Update product sections
				updateSourceFromDestination('stockInfo', 'outerHTML')
				updateSourceFromDestination('Subscriptions', 'outerHTML')
				updateSourceFromDestination('stickyATC', 'outerHTML')
			}

			/**
			 * Updates variant option selectors
			 * @param {Document} html - Parsed HTML document
			 */
			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')

				if (variantSelects) {
					LTA_HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			/**
			 * Updates the URL with new variant and selling plan parameters
			 * @param {string} url - Base product URL
			 * @param {string} variantId - Selected variant ID
			 */
			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				const params = new URLSearchParams()
				if (variantId) params.append('variant', variantId)
				if (this.selectedSellingPlanId) params.append('selling_plan', this.selectedSellingPlanId)

				const newUrl = `${url}?${params.toString()}`
				window.history.replaceState({}, '', newUrl)
			}

			/**
			 * Updates hidden variant inputs in all product forms
			 * @param {string} variantId - Selected variant ID
			 */
			updateVariantInputs(variantId) {
				this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			/**
			 * Sets the product as unavailable
			 */
			setUnavailable() {
				this.productFormEl?.global_toggleSubmitButton(true, 'unavailable')

				// Hide product information sections
				const selectors = ['price', 'stockInfo', 'Subscriptions', 'stickyATC']
					.map((id) => `#${id}-${this.dataset.section}`)
					.join(', ')

				document.querySelectorAll(selectors).forEach((el) => {
					el.classList.add('l-hidden')
				})
			}

			/**
			 * Gets the currently selected variant
			 * @param {HTMLElement} productInfoNode - Product info element
			 * @returns {Object|null} Selected variant object or null
			 */
			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('l-variant-selects [data-selected-variant]')?.innerHTML

				return selectedVariant ? JSON.parse(selectedVariant) : null
			}

			/**
			 * Builds a URL with query parameters for fetching product data
			 * @param {string} url - Base product URL
			 * @param {Array} optionValues - Selected option values
			 * @param {Object} variant - Selected variant
			 * @param {boolean} shouldFetchFullPage - Whether to fetch the full page
			 * @returns {string} URL with query parameters
			 */
			buildRequestUrlWithParams(url, optionValues, variant, shouldFetchFullPage = false) {
				const params = []

				// Add section ID if not fetching full page
				if (!shouldFetchFullPage) {
					params.push(`section_id=${this.sectionId}`)
				}

				// Add selling plan if selected
				if (this.selectedSellingPlanId) {
					params.push(`selling_plan=${this.selectedSellingPlanId}`)
				}

				// Add option values or variant ID
				if (optionValues?.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				} else if (variant) {
					params.push(`variant=${variant.id}`)
				}

				return `${url}?${params.join('&')}`
			}

			/**
			 * Gets the product form element
			 * @returns {HTMLElement} Product form element
			 */
			get productForm() {
				return this.querySelector('l-product-form')
			}

			/**
			 * Gets the variant selectors element
			 * @returns {HTMLElement} Variant selectors element
			 */
			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			/**
			 * Gets the section ID
			 * @returns {string} Section ID
			 */
			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LTAProductForm extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Cache DOM elements
				this.form = this.querySelector('form') || document.getElementById(this.dataset.formId)
				this.cart = document.getElementById('CartDrawer')
				this.submitButtonEl = this.querySelector('[type="submit"]')
				this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')

				// Store section data
				this.sectionId = this.dataset.sectionId
				this.productId = this.dataset.productId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Initialize event listeners
				if (this.form) {
					this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
				}

				document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this.handleOnVariantChange)
			}

			handleOnVariantChange(event) {
				const { sectionId, html, productId } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				const newSubmitButtonEl = html.getElementById(`ProductSubmitButton-${this.sectionId}`)

				if (!newSubmitButtonEl) return

				const priceEl = newSubmitButtonEl.querySelector('[data-product-normal-price]')
				if (priceEl) priceEl.innerHTML = ''

				this.submitButtonEl.replaceWith(newSubmitButtonEl)

				// Re-query elements
				this.submitButtonEl = this.querySelector('[type="submit"]')
				this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
			}

			/**
			 * Handles form submission
			 * @param {Event} evt - Form submit event
			 */
			onSubmitHandler(evt) {
				evt.preventDefault()

				// Don't submit if button is disabled
				if (this.submitButtonEl.getAttribute('aria-disabled') === 'true') return

				// Set button to loading state
				this.submitButtonEl.setAttribute('aria-disabled', true)
				this.submitButtonEl.classList.add('is-loading')

				// Create cart configuration and add to cart
				const config = ltaCartService.createConfigFromForm(this.form)

				ltaCartService
					.addToCart(config, 'l-product-form')
					.catch((e) => {
						console.error('Add to cart error:', e)
					})
					.finally(() => {
						// Remove loading state
						l_debounce(() => {
							this.submitButtonEl.classList.remove('is-loading')
						}, 400)()
						if (!this.error) this.submitButtonEl.removeAttribute('aria-disabled')
					})
			}

			/**
			 * Displays or hides error messages
			 * @param {string|boolean} errorMessage - Error message or false to hide
			 */
			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				// Find error message elements if not already cached
				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper?.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				// Toggle error message visibility
				this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage
			}

			/**
			 * Toggles the submit button state
			 * @param {boolean} disable - Whether to disable the button
			 * @param {string} textType - Type of text to display
			 * @param {boolean} needToUpdateText - Whether to update the button text
			 */
			global_toggleSubmitButton(disable = true, textType, needToUpdateText = true) {
				if (disable) {
					this.submitButtonEl.setAttribute('disabled', 'disabled')
					if (textType && needToUpdateText) this.submitButtonTextEl.textContent = this.getButtonLabel(textType)

					return
				}

				this.submitButtonEl.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonTextEl.textContent = this.getButtonLabel('addToCart')
				}
			}

			/**
			 * Gets the button label based on state
			 * @param {string} textType - Type of label to get
			 * @returns {string} Button label text
			 */
			getButtonLabel(textType = 'addToCart') {
				switch (textType) {
					case 'addToCart':
						return this.dataset.atcLabel || window.LTATheme.variantStrings.addToCart
					case 'soldOut':
						return this.dataset.soldOutLabel || window.LTATheme.variantStrings.soldOut
					case 'preOrder':
						return this.dataset.preOrderLabel || window.LTATheme.variantStrings.preOrder
					case 'unavailable':
						return this.dataset.unavailableLabel || window.LTATheme.variantStrings.unavailable
					default:
						return window.LTATheme.variantStrings.addToCart
				}
			}

			/**
			 * Gets the variant ID input element
			 * @returns {HTMLElement} Variant ID input
			 */
			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	customElements.define(
		'l-variant-selects',
		class LTAVariantSelect extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId

				// Events
				this.addEventListener('change', (event) => {
					const target = this.getInputForEventTarget(event.target)

					// Update visual state
					this.updateSelectionMetadata(event)

					// Notify about option change
					document.dispatchEvent(
						new CustomEvent('l:variant:option-changed', {
							detail: {
								event,
								target,
								selectedOptionValues: this.selectedOptionValues,
								productId: this.productId,
								sectionId: this.sectionId,
							},
						})
					)
				})
			}

			/**
			 * Updates visual indicators for the selected option
			 * @param {Event} event - Change event
			 */
			updateSelectionMetadata({ target }) {
				const { value, tagName } = target

				// Skip for select elements
				if (tagName === 'SELECT' && target.selectedOptions.length) {
					return
				}

				// Update selected value display for swatch inputs
				if (target.hasAttribute('data-product-variant-input')) {
					const selectedSwatchValue = target.closest('[data-variant-options]').querySelector('[data-selected-value]')

					if (selectedSwatchValue) {
						selectedSwatchValue.innerHTML = value
					}
				}
			}

			/**
			 * Gets the appropriate input element from an event target
			 * @param {HTMLElement} target - Event target element
			 * @returns {HTMLElement} Input element
			 */
			getInputForEventTarget(target) {
				return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
			}

			/**
			 * Gets the array of selected option values
			 * @returns {Array} Selected option value IDs
			 */
			get selectedOptionValues() {
				return Array.from(
					this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
				).map(({ dataset }) => dataset.optionValueId)
			}
		}
	)
}

if (!customElements.get('l-product-quantity-input')) {
	customElements.define(
		'l-product-quantity-input',
		class LTAProductQuantityInput extends HTMLElement {
			/**
			 * Creates a new quantity input component
			 */
			constructor() {
				super()
				this.changeEvent = new Event('change', { bubbles: true })
			}

			/**
			 * Event unsubscriber reference to allow cleanup
			 * @type {Function|undefined}
			 */
			quantityUpdateUnsubscriber = undefined

			/**
			 * Sets up component when connected to DOM
			 */
			connectedCallback() {
				// Cache DOM elements
				this.input = this.querySelector('input')
				this.buttonMinus = this.querySelector("button[name='minus']")
				this.buttonPlus = this.querySelector("button[name='plus']")

				// Bail if no input found
				if (!this.input) return

				// Setup event listeners
				this.setupEventListeners()

				// Initialize state
				this.validateQtyRules()
			}

			/**
			 * Attaches all required event listeners
			 */
			setupEventListeners() {
				// Input change handler
				this.input.addEventListener('change', () => this.validateQtyRules())

				// Button click handlers
				this.querySelectorAll('button').forEach((button) =>
					button.addEventListener('click', this.onButtonClick.bind(this))
				)

				// Global quantity update handler
				document.addEventListener('l:quantity:update', this.validateQtyRules.bind(this))
			}

			/**
			 * Handles button click events to increase/decrease quantity
			 * @param {Event} event - The click event
			 */
			onButtonClick(event) {
				event.preventDefault()
				const previousValue = this.input.value
				const buttonName = event.target.name

				if (buttonName === 'plus') {
					this.incrementQuantity()
				} else if (buttonName === 'minus') {
					this.decrementQuantity()
				}

				// Dispatch change event if value changed
				if (previousValue !== this.input.value) {
					this.input.dispatchEvent(this.changeEvent)
				}
			}

			/**
			 * Increases the quantity value
			 */
			incrementQuantity() {
				const needToResetMinimumValue =
					parseInt(this.input.dataset.min) > parseInt(this.input.step) && parseInt(this.input.value) === 0

				if (needToResetMinimumValue) {
					this.input.value = this.input.dataset.min
				} else {
					this.input.stepUp()
				}
			}

			/**
			 * Decreases the quantity value
			 */
			decrementQuantity() {
				this.input.stepDown()

				// Reset to minimum if needed
				if (this.input.dataset.min === this.input.value) {
					this.input.value = parseInt(this.input.min)
				}
			}

			/**
			 * Validates quantity against min/max rules and updates button states
			 */
			validateQtyRules() {
				const value = parseInt(this.input.value)

				// Validate against minimum
				if (this.input.min && this.buttonMinus) {
					this.buttonMinus.classList.toggle('disabled', value <= parseInt(this.input.min))
				}

				// Validate against maximum
				if (this.input.max && this.buttonPlus) {
					this.buttonPlus.classList.toggle('disabled', value >= parseInt(this.input.max))
				}
			}

			/**
			 * Cleans up event listeners when disconnected
			 */
			disconnectedCallback() {
				document.removeEventListener('l:quantity:update', this.validateQtyRules)
				if (this.quantityUpdateUnsubscriber) {
					this.quantityUpdateUnsubscriber()
				}
			}
		}
	)
}

if (!customElements.get('l-product-controls')) {
	customElements.define(
		'l-product-controls',
		class LTAProductControls extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.quantityFormEl = this.querySelector('l-product-quantity-input')
				this.quantityInputEl = this.quantityFormEl?.querySelector('input')
				this.productFormEl = this.querySelector('l-product-form')

				if (!this.quantityInputEl || !this.quantityFormEl) return

				// Data
				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId

				// Functions
				this.setQuantityBoundries()

				// Events
				document.addEventListener('l:variant:changed', this.updateQuantityRules.bind(this))
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this.updateQuantityRules)
			}

			/**
			 * Sets initial quantity input boundaries based on variant data
			 */
			setQuantityBoundries() {
				// Get quantity data
				const quantityDataset = this.quantityInputEl.dataset
				const data = {
					cartQuantity: quantityDataset.cartQuantity ? parseInt(quantityDataset.cartQuantity) : 0,
					min: quantityDataset.min ? parseInt(quantityDataset.min) : 1,
					max: quantityDataset.max ? parseInt(quantityDataset.max) : null,
					step: this.quantityInputEl.step ? parseInt(this.quantityInputEl.step) : 1,
				}

				// Calculate min and max values
				let min = data.min
				const max = data.max === null ? data.max : data.max - data.cartQuantity

				if (max !== null) {
					min = Math.min(min, max)
				}

				if (data.cartQuantity >= data.min) {
					min = Math.min(min, data.step)
				}

				// Update input attributes
				this.quantityInputEl.min = min
				this.quantityInputEl.value = min

				if (max) {
					this.quantityInputEl.max = max
				} else {
					this.quantityInputEl.removeAttribute('max')
				}

				// Notify about quantity update
				document.dispatchEvent(
					new CustomEvent('l:quantity:update', {
						detail: { id: this.dataset.section },
					})
				)
			}

			/**
			 * Updates quantity rules when variant changes
			 * @param {CustomEvent} event - Variant change event
			 */
			updateQuantityRules(event) {
				const { html, sectionId, productId } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				// Get updated quantity form
				const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
				if (!quantityFormUpdated) return

				// Update selectors
				const selectors = ['[data-product-quantity-input]', '[data-quantity-label]']

				for (let selector of selectors) {
					const current = this.querySelector(selector)
					const updated = quantityFormUpdated.querySelector(selector)

					if (!current || !updated) continue

					// Update product quantity input attributes
					if (selector === '[data-product-quantity-input]') {
						const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']

						for (let attribute of attributes) {
							const valueUpdated = updated.getAttribute(attribute)

							if (valueUpdated !== null) {
								current.setAttribute(attribute, valueUpdated)
							} else {
								current.removeAttribute(attribute)
							}
						}
					} else {
						// Update HTML content for other elements
						current.innerHTML = updated.innerHTML
					}
				}

				// Update quantity boundaries and dispatch event
				this.setQuantityBoundries()
			}
		}
	)
}

// Note: placed this component at the end of the file to avoid race conditions
if (!customElements.get('l-product-price')) {
	class LTAProductPrice extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Data
			this.sectionId = this.dataset.sectionId
			this.productId = this.dataset.productId
			this.addOnPrice = {}

			this.priceData = JSON.parse(this.querySelector('script[type="application/json"]').textContent)

			// Elements
			this.priceEl = this.querySelector('[data-price]')
			this.compareAtPriceEl = this.querySelector('[data-compare-at-price]')
			this.saleTagEl = this.querySelector('[data-sale-tag]')

			this.globalNormalPriceEls = document.querySelectorAll(`[data-product-normal-price="${this.sectionId}"]`)

			// Events
			document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
			document.addEventListener('l:addon-products:changed', this.handleOnAddOnChange.bind(this))
		}

		disconnectedCallback() {
			document.removeEventListener('l:variant:changed', this.handleOnVariantChange)
			document.removeEventListener('l:addon-products:changed', this.handleOnAddOnChange)
		}

		handleOnVariantChange(event) {
			const { sectionId, html, productId } = event.detail

			if (productId !== this.productId || sectionId !== this.sectionId) return

			// Update price data
			const newPriceEl = html.getElementById(`price-${sectionId}`)
			this.priceData = JSON.parse(newPriceEl.querySelector('script[type="application/json"]').textContent)

			// Update price
			const priceData = this.calculatePrices()
			this.updatePriceHTML(priceData)
		}

		handleOnAddOnChange(event) {
			const { id, totalNormalPrice, totalComparePrice, productId, sectionId } = event.detail

			if (productId !== this.productId || sectionId !== this.sectionId) return

			this.addOnPrice[id] = {
				totalNormalPrice,
				totalComparePrice,
			}

			const priceData = this.calculatePrices()
			this.updatePriceHTML(priceData)
		}

		updatePriceHTML(priceData) {
			// Data
			const {
				comparePrice,
				normalPrice,
				newNormalPriceFormatted,
				newComparePriceFormatted,
				salePercentage,
				salePriceFormatted,
				saleTagLabel,
			} = priceData

			// Update price elements
			this.priceEl.innerHTML = newNormalPriceFormatted

			this.compareAtPriceEl.classList.toggle('l-hidden', !comparePrice)
			if (comparePrice) {
				this.compareAtPriceEl.innerHTML = newComparePriceFormatted
			}

			// Update sale tag
			if (this.saleTagEl && saleTagLabel) {
				this.saleTagEl.classList.toggle('l-hidden', !comparePrice)

				this.saleTagEl.innerHTML = saleTagLabel
					.replace('[percentage]', `${salePercentage}%`)
					.replace('[amount]', salePriceFormatted)
			}

			// Update global normal price elements
			const globalNormalPriceEls = document.querySelectorAll(`[data-product-normal-price="${this.sectionId}"]`)

			globalNormalPriceEls.forEach((globalNormalPriceEl) => {
				globalNormalPriceEl.innerHTML = newNormalPriceFormatted
			})
		}

		calculatePrices() {
			const { normalPrice, comparePrice, saleTagLabel } = this.priceData

			const totalAddOnNormalPrice = this.getTotalAddOnNormalPrice
			const totalAddOnComparePrice = this.getTotalAddOnComparePrice

			const newNormalPrice = normalPrice + totalAddOnNormalPrice
			const newComparePrice = comparePrice + totalAddOnComparePrice

			const newNormalPriceFormatted = l_formatMoney(newNormalPrice)
			const newComparePriceFormatted = l_formatMoney(newComparePrice)

			const salePercentage = parseFloat(((newComparePrice - newNormalPrice) / newComparePrice) * 100).toFixed(0)
			const salePriceFormatted = l_formatMoney(newComparePrice - newNormalPrice)

			return {
				normalPrice,
				comparePrice,
				salePercentage,
				newNormalPriceFormatted,
				newComparePriceFormatted,
				salePriceFormatted,
				saleTagLabel,
			}
		}

		get getTotalAddOnNormalPrice() {
			return Object.values(this.addOnPrice).reduce((acc, curr) => acc + curr.totalNormalPrice, 0)
		}

		get getTotalAddOnComparePrice() {
			return Object.values(this.addOnPrice).reduce((acc, curr) => acc + curr.totalComparePrice, 0)
		}
	}

	customElements.define('l-product-price', LTAProductPrice)
}

/************************************************************************************
 *
 * [MODULE]: CUSTOM PRODUCT OPTIONS MODULE
 *
 *************************************************************************************/
if (!customElements.get('l-product-custom-options')) {
	class LTAProductCustomOptions extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			// Data
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			this.productOptionsData = JSON.parse(this.querySelector('script[type="application/json"]').textContent)
			this.cachedHTML = {}

			// Elements
			this.formEl = this.querySelector('form')

			// Product Sections
			this.productEl = document.getElementById('product')
			this.benefitSectionId = this.getSectionId(`.js-product-benefits`)
			this.compareCardSectionId = this.getSectionId(`.js-compare-cards`)
			this.productInfoSectionId = this.getSectionId(`.js-product-faqs`)

			this.sectionIds = [
				this.sectionId,
				this.benefitSectionId,
				this.compareCardSectionId,
				this.productInfoSectionId,
			].map((sectionId) => sectionId)

			// Events
			this.addEventListener('change', this.handleOnChange.bind(this))
		}

		handleOnChange(event) {
			// Get form data
			const formData = new FormData(this.formEl)

			const productOption1 = formData.get(`custom_option_1`)
			const productOption2 = formData.get(`custom_option_2`)
			const productOption3 = formData.get(`custom_option_3`)

			// Find selected product
			const optionKeys = ['custom_option_1', 'custom_option_2', 'custom_option_3']
			const selectedOptions = {
				custom_option_1: productOption1,
				custom_option_2: productOption2,
				custom_option_3: productOption3,
			}

			const selectedProduct = Object.entries(this.productOptionsData).find(([productId, productOptions]) => {
				return optionKeys.every((optKey) => {
					if (
						typeof productOptions[optKey] !== 'undefined' &&
						productOptions[optKey] !== null &&
						productOptions[optKey] !== ''
					) {
						return selectedOptions[optKey] === productOptions[optKey]
					}
					return true
				})
			})

			console.log(Object.entries(this.productOptionsData))
			console.log(selectedProduct)

			if (!selectedProduct) return

			const productURL = selectedProduct[1].product_url

			// Setup loading status
			this.classList.add('is-loading')

			// Fetch new product
			const isFetchedProduct = this.cachedHTML[productURL]

			isFetchedProduct ? this.updateProductSections(isFetchedProduct, productURL) : this.fetchNewProduct(productURL)
		}

		fetchNewProduct(productURL) {
			fetch(`${productURL}?sections=${this.sectionIds.join(',')}`)
				.then((response) => response.json())
				.then((data) => {
					this.updateProductSections(data, productURL)
				})
		}

		updateProductSections(data, productURL) {
			// Update main product section
			const newProductEl = new DOMParser().parseFromString(data[this.sectionId], 'text/html').querySelector('#product')
			if (newProductEl) {
				// Preload images
				const images = newProductEl.querySelectorAll('img')
				images.forEach((img) => {
					l_preloadImage(img.src)
				})

				// Replace section
				this.productEl.replaceWith(newProductEl)
			}

			// Update other sections
			this.sectionIds.forEach((sectionId) => {
				const newSectionEl = new DOMParser().parseFromString(data[sectionId], 'text/html')
				const sectionEl = document.getElementById(sectionId)

				if (sectionEl) sectionEl.replaceWith(newSectionEl)
			})

			// Cache HTML
			this.cachedHTML[productURL] = data

			// Update URL
			this.updateURL(productURL)

			// Remove loading status
			this.classList.remove('is-loading')
		}

		getSectionId(selector) {
			return document.querySelector(selector)?.parentElement?.id.replace('shopify-section-', '')
		}

		updateURL(productURL) {
			window.history.replaceState({}, '', productURL)
		}
	}

	customElements.define('l-product-custom-options', LTAProductCustomOptions)
}

/************************************************************************************
 *
 * [MODULE]: PRODUCT MEDIA
 *
 *************************************************************************************/

if (!customElements.get('l-product-media-gallery')) {
	customElements.define(
		'l-product-media-gallery',
		class ProductMediaGallery extends HTMLElement {
			constructor() {
				super()

				this.sectionId = this.dataset.sectionId
			}

			connectedCallback() {
				// Data
				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId

				// Events
				if (window.Swiper) {
					this.setupCarousel()
				} else {
					document.addEventListener('l:vendor:loaded', this.setupCarousel.bind(this))
				}

				document.addEventListener('l:variant:changed', this.onVariantChange.bind(this))
			}

			disconnectedCallback() {
				document.removeEventListener('l:vendor:loaded', this.setupCarousel)
				document.removeEventListener('l:variant:changed', this.onVariantChange)
			}

			setupCarousel() {
				// Setup carousel for thumbnail first
				// this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				// this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')

				// this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				// this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
				// 	loop: false,
				// 	slidesPerView: 'auto',
				// 	spaceBetween: 8,
				// 	resistanceRatio: 0.2,
				// 	navigation: {
				// 		nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
				// 		prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
				// 	},
				// })

				// Setup carousel for main media
				this.mainCarouselEl = this.querySelector('[data-main-carousel]')
				this.mediaSlidesEls = this.mainCarouselEl.querySelectorAll('[data-media]')
				this.navigationNextEl = this.mainCarouselEl.querySelector('[data-next]')
				this.navigationPrevEl = this.mainCarouselEl.querySelector('[data-prev]')
				this.paginationEl = this.mainCarouselEl.querySelector('[data-pagination]')

				this.mediaSlidesEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarouselEl, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 16,
					autoHeight: true,
					keyboard: {
						enabled: true,
					},
					pagination: {
						el: this.paginationEl,
						clickable: true,
						dynamicBullets: true,
						dynamicMainBullets: 5,
					},
					navigation: {
						nextEl: this.navigationNextEl,
						prevEl: this.navigationPrevEl,
					},
					breakpoints: {
						560: {
							spaceBetween: 0,
						},
					},
					// thumbs: {
					//   swiper: this.thumbnailCarouselSwiper,
					// },
				})

				// Setup controller
				// this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper

				// Setup changed events
				this.handleSlideChangeEvent()
			}

			handleSlideChangeEvent() {
				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = this.mediaSlidesEls[swiper.activeIndex]

					l_pauseAllMedia()
					l_playMedia(activeSlideEl)
				})
			}

			onVariantChange(event) {
				const { sectionId, productId, variant } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				const featuredMediaId = variant?.featured_media?.id
				const activeSlideIndex = featuredMediaId
					? Array.from(this.mediaSlidesEls).findIndex(
							(mediaSlideEl) => mediaSlideEl.dataset.mediaId == `${featuredMediaId}`
						)
					: 0

				this.mainCarouselSwiper.slideTo(activeSlideIndex, true)
			}
		}
	)
}

/******************************
 * MODULE: MODAL GALLERY
 *******************************/

if (!customElements.get('l-product-modal-gallery')) {
	customElements.define(
		'l-product-modal-gallery',
		class LTAProductModalGallery extends LTADrawer {
			connectedCallback() {
				super.connectedCallback()

				// Elements
				this.mediaEls = this.querySelectorAll('[data-media]')

				// Events
				document.addEventListener('l:vendor:loaded', () => {
					this.setupCarousel()
				})

				document.addEventListener('l:media:click', (event) => {
					// Open the active media
					const mediaId = event.detail.mediaId
					this.mediaEls.forEach((mediaEl, index0) => {
						const _mediaId = mediaEl.getAttribute('data-media-id')
						if (_mediaId == mediaId) {
							this.mainCarouselSwiper.slideTo(index0)
						}
					})

					// Open the modal
					this.drawer_open()
				})
			}

			setupCarousel() {
				// Setup carousel for thumbnail first
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					navigation: {
						nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
						prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
					},
				})

				// Setup carousel for main media
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 0,
					autoHeight: true,
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})

				// Setup controller
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper

				// Setup changed events
				this.handleSlideChangeEvent()
			}

			handleSlideChangeEvent() {
				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = swiper.slides[swiper.activeIndex]

					l_pauseAllMedia(this)
					l_playMedia(activeSlideEl)
				})
			}
		}
	)
}

if (!customElements.get('l-media-zoom')) {
	customElements.define(
		'l-media-zoom',
		class LTAMediaZoom extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				document.addEventListener('l:zoomifyJs:loaded', () => {
					this.setupZoomMedia()
				})
			}

			setupZoomMedia() {
				const zoomify = new ZoomifyJs({
					selector: this.querySelector('.js-zoomifyJs-image'),
					transitionDuration: 300,
					easing: 'ease-in-out',
					scale: 2,
					clickToZoom: true,
				})
			}
		}
	)
}

if (!customElements.get('l-product-media-single')) {
	customElements.define(
		'l-product-media-single',
		class LTAMediaSingle extends LTADrawerTrigger {
			connectedCallback() {
				super.connectedCallback()

				this.addEventListener('click', (event) => {
					this.dispatchEvent(
						new CustomEvent('l:media:click', { bubbles: true, detail: { mediaId: this.dataset.mediaId } })
					)
				})
			}
		}
	)
}

/************************************************************************************
 *
 * [SECTIONS]: PRODUCT ADDITIONAL COMPONENTS
 *
 *************************************************************************************/

/**********************************
 * [PRODUCT BLOCK]: UPSELL PRODUCT
 ***********************************/

if (!customElements.get('l-upsell-products')) {
	customElements.define(
		'l-upsell-products',
		class LTAProductUpsells extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				if (this.dataset.url) this.loadRecommendations()
			}

			loadRecommendations() {
				const productListEl = this.querySelector('[data-products-list]')

				fetch(this.dataset.url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('.shopify-section')

						if (productListEl) {
							// Update atc buttons label
							recommendations.querySelectorAll('[data-atc]').forEach((atcBtn) => {
								atcBtn.innerHTML = this.dataset.atcBtnLabel
							})

							// Insert recommendations to product list
							productListEl.innerHTML = recommendations.innerHTML
							this.classList.remove('is-loading')
						}
					})
					.catch((e) => {
						this.remove()
					})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: PRODUCT ADD ONS

***********************************/
if (!customElements.get('l-product-addons')) {
	customElements.define(
		'l-product-addons',
		class LTAProductAddOns extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.variantEls = this.querySelectorAll('[data-variant]')

				// Events
				this.handleOnVariantCheck()
			}

			handleOnVariantCheck() {
				this.variantEls.forEach((variantEl) => {
					variantEl.addEventListener('click', (event) => {
						// Update selected variant
						const inputEl = variantEl.querySelector('input')
						const isChecked = variantEl.classList.toggle('is-checked')
						inputEl.toggleAttribute('disabled', !isChecked)

						// Calculate total price
						let totalNormalPrice = 0
						let totalComparePrice = 0

						this.variantEls.forEach((variantEl) => {
							const isChecked = variantEl.classList.contains('is-checked')
							const variantPrice = parseFloat(variantEl.dataset.variantPrice)
							const variantComparePrice = parseFloat(variantEl.dataset.variantComparePrice)

							totalNormalPrice += isChecked ? variantPrice : 0
							totalComparePrice += isChecked ? variantComparePrice : 0
						})

						this.dispatchEvent(
							new CustomEvent('l:addon-products:changed', {
								bubbles: true,
								detail: {
									id: this.dataset.sectionId,
									totalNormalPrice,
									totalComparePrice,
									productId: this.dataset.productId,
									sectionId: this.dataset.sectionId,
								},
							})
						)
					})
				})
			}
		}
	)
}

/**********************************
 * [PRODUCT BLOCK]: SUBSCRIPTIONS
 ***********************************/

if (!customElements.get('l-subscriptions-selling-plans')) {
	customElements.define(
		'l-subscriptions-selling-plans',
		class LTASubscriptionSellingPlans extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.init()

				// Data
				this.productId = this.dataset.productId
				this.sectionId = this.dataset.sectionId
				this.subscriptionData = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent)
				this.selectedVariantId = this.subscriptionData.id
				this.selectedSellingPlanId = this.querySelector('[name="selling_plan"]:checked')?.value

				// Setup functions
				this.optional__disableAppInputs()

				// Events
				document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
				this.addEventListener('change', this.handleOnChange.bind(this))
			}

			init() {
				// Elements
				this.subscriptionOptionEls = this.querySelectorAll('[data-subscription-option]')
			}

			handleOnVariantChange(event) {
				const { sectionId, html, productId } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				const newSubscriptionsEl = html.getElementById(`Subscriptions-${this.sectionId}`)

				// Update subscriptions
				if (newSubscriptionsEl) {
					this.innerHTML = newSubscriptionsEl.innerHTML

					// Re-init elements
					this.init()

					return
				}

				// Hide subscriptions
				this.classList.add('l-hidden')
			}

			handleOnChange({ target }) {
				if (!target.hasAttribute('name') || !target.getAttribute('name') === 'selling_plan') return

				this.selectedSellingPlanId = target.value
			}

			dispatchSellingPlanIdChanged() {
				this.dispatchEvent(
					new CustomEvent('l:sellingPlan:changed', {
						bubbles: true,
						detail: {
							sellingPlanId: this.selectedSellingPlanId,
							productId: this.productId,
							sectionId: this.sectionId,
							selectedVariantId: this.selectedVariantId,
							target: this,
						},
					})
				)
			}

			optional__disableAppInputs() {
				document.addEventListener('sealsubs:subscription_widget_created', function (e) {
					const productEl = document.getElementById('product')
					const sealEls = productEl.querySelectorAll('.sealsubs-target-element')

					sealEls.forEach((sealEl) => {
						sealEl.classList.add('l-hidden')

						const sellingPlanInputs = sealEl.querySelectorAll('[name="selling_plan"]')
						sellingPlanInputs.forEach((input) => {
							input.setAttribute('disabled', 'disabled')
						})
					})
				})
			}
		}
	)
}
/**********************************
 * [PRODUCT BLOCK]: STICKY ATC
 ***********************************/

if (!customElements.get('l-product-sticky-atc')) {
	customElements.define(
		'l-product-sticky-atc',
		class LTAProductStickyATC extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Move to body
				if (this.parentElement !== document.body) {
					document.body.appendChild(this)

					return
				}

				// Elements
				this.submitATCEls = this.querySelectorAll('[type="submit"]')
				this.mainSubmitEl = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`)

				// Handle submit atc button click
				this.submitATCEls.forEach((submitATCEl) => {
					submitATCEl.addEventListener('click', () => {
						submitATCEl.classList.add('is-loading')
					})
				})

				// Handle cart update
				document.addEventListener('l:cart:updated', this.handleOnCartUpdate.bind(this))

				// Handle scroll (default)
				this.checkStickyATC()

				// Handle scroll
				this.debouncedCheckStickyATC = l_debounce(this.checkStickyATC.bind(this), 50)
				document.addEventListener('scroll', this.debouncedCheckStickyATC)
			}

			disconnectedCallback() {
				document.removeEventListener('scroll', this.debouncedCheckStickyATC)
				document.removeEventListener('l:cart:updated', this.handleOnCartUpdate.bind(this))
			}

			handleOnCartUpdate() {
				this.submitATCEls.forEach((submitATCEl) => {
					submitATCEl.classList.remove('is-loading')
				})
			}

			checkStickyATC() {
				const rect = this.mainSubmitEl.getBoundingClientRect()
				const scrollHeight = document.documentElement.scrollHeight
				const screenHeight = window.innerHeight
				const scrollPosition = window.scrollY || document.documentElement.scrollTop

				/*
				 * Condition:
				 * 1. Main atc button is of out screen
				 * 2. Sticky atc button is not at the bottom of the page
				 */
				const isVisible =
					rect.bottom <= window.innerHeight &&
					rect.top < -100 &&
					scrollPosition + screenHeight + this.offsetHeight < scrollHeight

				this.classList.toggle('is-sticky', isVisible)
			}
		}
	)
}

/**********************************
 * [PRODUCT BLOCK]: ADDED TO CART MODAL
 ***********************************/

if (!customElements.get('l-product-added-to-cart')) {
	customElements.define(
		'l-product-added-to-cart',
		class LTAProductAddedToCart extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.drawerEl = this.closest('l-drawer')
				this.variantEls = this.querySelectorAll('[data-variant-id]')
				this.viewCartBtnEl = this.querySelector('[data-view-cart]')

				// Events
				document.addEventListener('l:cart:updated', this.handleOnCartUpdate.bind(this))
				this.viewCartBtnEl.addEventListener('click', this.handleOnViewCart.bind(this))
			}

			disconnectedCallback() {
				document.removeEventListener('l:cart:updated', this.handleOnCartUpdate.bind(this))
				this.viewCartBtnEl.removeEventListener('click', this.handleOnViewCart.bind(this))
			}

			handleOnViewCart() {
				window.Rebuy?.SmartCart?.hide?.()
				this.drawerEl.drawer_close()

				requestAnimationFrame(() => {
					window.Rebuy?.SmartCart?.show?.()
				})
			}

			handleOnCartUpdate(event) {
				const variant = event.detail.response

				// Select the variant that is added to cart
				this.variantEls.forEach((variantEl) => {
					variantEl.classList.toggle('l-hidden', variantEl.dataset.variantId !== `${variant.id}`)
				})

				// Show the drawer
				this.drawerEl.drawer_open()
			}
		}
	)
}

/************************************************************************************
 *
 * [SECTIONS]: PRODUCT ADDITIONAL SECTIONS
 *
 *************************************************************************************/

/******************************
 * SECTION: PRODUCT RECOMMENDATIONS
 *******************************/

if (!customElements.get('l-product-recommendations')) {
	customElements.define(
		'l-product-recommendations',
		class LTAProductRecommendations extends HTMLElement {
			observer = undefined

			constructor() {
				super()
			}

			connectedCallback() {
				this.initializeRecommendations()
			}

			initializeRecommendations() {
				this.observer?.unobserve(this)
				this.observer = new IntersectionObserver(
					(entries, observer) => {
						if (!entries[0].isIntersecting) return
						observer.unobserve(this)
						this.loadRecommendations()
					},
					{ rootMargin: '0px 0px 400px 0px' }
				)
				this.observer.observe(this)
			}

			loadRecommendations() {
				console.log(this.dataset.url)

				fetch(this.dataset.url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('l-product-recommendations')

						if (recommendations?.innerHTML.trim().length) {
							this.innerHTML = recommendations.innerHTML
						}
					})
					.catch((e) => {
						console.error(e)
					})
			}
		}
	)
}

